import PropTypes from 'prop-types';

import { useEffect, useState } from 'react';

// @mui
import {
  Box,
  Radio,
  Stack,
  Button,
  Drawer,
  Divider,
  IconButton,
  Typography,
  RadioGroup,
  FormControlLabel,
  alpha,
  Grid,
  Chip,
} from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';

import api from '../../../services/api';

// ----------------------------------------------------------------------

const typeColors = {
  bug: '#92bc2c',
  dark: '#595761',
  dragon: '#0c69c8',
  electric: '#f2d94e',
  fairy: '#e91e63',
  fighting: '#d3425f',
  fire: '#fba54c',
  flying: '#a1bbec',
  ghost: '#5f6dbc',
  grass: '#5fbd58',
  ground: '#da7c4d',
  ice: '#75d0c1',
  normal: '#a0a29f',
  poison: '#b763cf',
  psychic: '#fa8581',
  rock: '#c9bb8a',
  steel: '#5695a3',
  water: '#539ddf',
};

export default function PokemonFilterSidebar({ openFilter, onOpenFilter, onCloseFilter, onTypeChange }) {
  const [typesFilters, setTypesFilter] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedGeneration, setSelectedGeneration] = useState('');

  const generations = [
    { name: 'generation-i', label: 'Generation I' },
    { name: 'generation-ii', label: 'Generation II' },
    { name: 'generation-iii', label: 'Generation III' },
    { name: 'generation-iv', label: 'Generation IV' },
    { name: 'generation-v', label: 'Generation V' },
    { name: 'generation-vi', label: 'Generation VI' },
    { name: 'generation-vii', label: 'Generation VII' },
    { name: 'generation-viii', label: 'Generation VIII' },
  ];

  useEffect(() => {
    getTypesFilter();
  }, []);

  function getTypesFilter() {
    api
      .get('type')
      .then((res) => {
        // Filtra apenas os tipos principais (remove tipos especiais como shadow, unknown)
        const mainTypes = res.data.results.filter(type => 
          ['bug', 'dark', 'dragon', 'electric', 'fairy', 'fighting', 'fire', 'flying', 
           'ghost', 'grass', 'ground', 'ice', 'normal', 'poison', 'psychic', 'rock', 
           'steel', 'water'].includes(type.name)
        );
        setTypesFilter(mainTypes);
      })
      .catch(() => {
        // Error handling silently
      });
  }

  const handleTypeToggle = (typeName) => {
    setSelectedTypes((prev) => {
      if (prev.includes(typeName)) {
        return prev.filter((t) => t !== typeName);
      }
      return [...prev, typeName];
    });
  };

  const filterPokemons = () => {
    // Passa o array de tipos selecionados
    onTypeChange(selectedTypes, selectedGeneration);
    onCloseFilter();
  };

  const clearFilters = () => {
    setSelectedTypes([]);
    setSelectedGeneration('');
    onTypeChange([], '');
  };

  return (
    <>
      <Button 
        fullWidth
        size="large"
        color="primary" 
        variant="contained"
        endIcon={<Iconify icon="ic:round-filter-list" />} 
        onClick={onOpenFilter}
        sx={{
          height: 56,
          fontSize: '1rem',
          fontWeight: 600,
          boxShadow: 2,
          '&:hover': {
            boxShadow: 4,
          },
        }}
      >
        Filters
      </Button>

      <Drawer
        anchor="right"
        open={openFilter}
        onClose={onCloseFilter}
        PaperProps={{
          sx: { 
            width: { xs: '100%', sm: 380 }, 
            maxWidth: 380,
            border: 'none', 
            overflow: 'hidden' 
          },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1, py: 2 }}>
          <Typography variant="subtitle1" sx={{ ml: 1 }}>
            Filters
          </Typography>
          <IconButton onClick={onCloseFilter}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Stack>

        <Divider />

        <Scrollbar>
          <Stack spacing={3} sx={{ p: 3 }}>
            <div>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Type
                </Typography>
                {selectedTypes.length > 0 && (
                  <Chip 
                    label={selectedTypes.length} 
                    size="small" 
                    color="primary"
                    sx={{ height: 20, fontSize: '0.75rem', fontWeight: 600 }}
                  />
                )}
              </Stack>
              
              <Grid container spacing={1}>
                {typesFilters.map((type) => (
                  <Grid item xs={6} key={type.name}>
                    <Box
                      onClick={() => handleTypeToggle(type.name)}
                      sx={{
                        position: 'relative',
                        cursor: 'pointer',
                        borderRadius: 2,
                        p: 1.5,
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        border: '2px solid',
                        borderColor: selectedTypes.includes(type.name) 
                          ? typeColors[type.name] 
                          : 'transparent',
                        bgcolor: selectedTypes.includes(type.name)
                          ? alpha(typeColors[type.name] || '#999', 0.15)
                          : alpha('#000', 0.02),
                        '&:hover': {
                          bgcolor: alpha(typeColors[type.name] || '#999', 0.1),
                          transform: 'translateY(-2px)',
                          boxShadow: `0 4px 12px ${alpha(typeColors[type.name] || '#999', 0.3)}`,
                        },
                      }}
                    >
                      {selectedTypes.includes(type.name) && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: typeColors[type.name],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          }}
                        >
                          ✓
                        </Box>
                      )}
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          mx: 'auto',
                          mb: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          filter: selectedTypes.includes(type.name) 
                            ? 'none' 
                            : 'brightness(0.3) grayscale(100%)',
                          transition: 'filter 0.3s ease',
                        }}
                      >
                        <img
                          src={`/assets/icons/type/${type.name}.svg`}
                          alt={type.name}
                          style={{ width: '100%', height: '100%' }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          textTransform: 'capitalize',
                          fontWeight: selectedTypes.includes(type.name) ? 700 : 500,
                          fontSize: '0.7rem',
                          color: selectedTypes.includes(type.name) 
                            ? typeColors[type.name]
                            : 'text.secondary',
                        }}
                      >
                        {type.name}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </div>

            <Divider />

            <div>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Generation
              </Typography>
              <RadioGroup value={selectedGeneration} onChange={(e) => setSelectedGeneration(e.target.value)}>
                {generations.map((item) => (
                  <FormControlLabel 
                    key={item.name} 
                    value={item.name} 
                    control={<Radio size="small" />} 
                    label={
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {item.label}
                      </Typography>
                    }
                  />
                ))}
              </RadioGroup>
            </div>
          </Stack>
        </Scrollbar>

        <Box sx={{ p: 3, bgcolor: (theme) => alpha(theme.palette.background.default, 0.5) }}>
          <Stack spacing={1.5}>
            <Button
              fullWidth
              size="large"
              type="submit"
              color="primary"
              variant="contained"
              onClick={filterPokemons}
              disabled={selectedTypes.length === 0 && !selectedGeneration}
              startIcon={<Iconify icon="ic:round-check" />}
              sx={{
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                },
              }}
            >
              Apply Filters
            </Button>
            <Button
              fullWidth
              size="medium"
              color="inherit"
              variant="text"
              onClick={clearFilters}
              startIcon={<Iconify icon="ic:round-clear" />}
            >
              Clear All
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
}

PokemonFilterSidebar.propTypes = {
  openFilter: PropTypes.bool.isRequired,
  onOpenFilter: PropTypes.func.isRequired,
  onCloseFilter: PropTypes.func.isRequired,
  onTypeChange: PropTypes.func.isRequired,
};
